import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useReducer, useState } from 'react';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import { Card, Stack } from '@optimus-web/core';
import { lt, showInfoAlert, useFeatureFlag, useLocalizeText } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import {
    CheckboxValues,
    CheckInCheckboxColumnName,
    CheckInCheckboxStates,
    CheckInFieldsTooltipValues,
    CheckInFieldsValues,
    ConfigurationEditableColumnValues,
} from '../types';
import { CheckInTabs } from '../CheckInTabs/CheckInTabs';
import { TabItemValue } from '../CheckInTabs/enums';
import { getTabs } from '../CheckInTabs/Tabs';
import { FieldData, supportedDocumentTypeOptions } from '../sections';
import { CheckInFieldsHeader } from '../CheckInFieldsHeader';
import { DocumentsTable } from '../documentsTable/DocumentsTable';
import { DocumentSectionTables } from '../documentsTable/DocumentSectionTables';
import { getSelectedDocumentType } from '../documentsTable/documentUtils';
import { ConfigurationTooltip } from '../tooltip/mapTooltipText';
import { getCheckInSameValues } from '../utils';
import { checkInFieldsReducer } from './checkInFieldsReducer';
import { CheckInFieldsTable } from './CheckInFieldsTable';
import { ChangeCheckboxesStateProps, VisibilityData } from './types';
import { calculateCheckboxesState } from './utils';

interface CheckInFieldsTableContainerProps {
    isLegallyRequiredRecord: Record<CheckInConfigurationType, boolean>;
    initialValues: CheckInFieldsValues;
    editableValues: ConfigurationEditableColumnValues;
    tooltipValues: CheckInFieldsTooltipValues;
    onSubmit: (values: CheckInFieldsValues, checkboxStates: CheckInCheckboxStates) => void;
}

interface SectionFeatureFlags {
    guestVerificationPhotoEnabled: boolean;
    areExtendedHotelConfiguredFieldsEnabled: boolean;
    isIdentityDocumentScanEnabled: boolean;
    isIdentityDocumentScanVerificationEnabled: boolean;
}

const getSection = (activeTableValue: TabItemValue, sectionFeatureFlags: SectionFeatureFlags): FieldData[] =>
    pipe(
        getTabs(sectionFeatureFlags),
        array.findFirst((tab) => tab.value === activeTableValue),
        option.fold(
            () => [],
            (tab) => tab.section
        )
    );

export const CheckInFieldsTableContainer = ({
    editableValues,
    isLegallyRequiredRecord,
    initialValues,
    tooltipValues,
    onSubmit,
}: CheckInFieldsTableContainerProps) => {
    const guestVerificationPhotoEnabled = useFeatureFlag(FeatureFlag.guestVerificationPhotoOciConfigurationEnabled);
    const areExtendedHotelConfiguredFieldsEnabled = useFeatureFlag(FeatureFlag.extendedHotelConfiguredFields);
    const isIdentityDocumentScanEnabled = useFeatureFlag(FeatureFlag.identityDocumentScanEnabled);
    const isIdentityDocumentScanVerificationEnabled = useFeatureFlag(
        FeatureFlag.identityDocumentScanVerificationEnabled
    );
    const [activeTabValue, setActiveTabValue] = useState(TabItemValue.General);
    const [{ selectedDocumentType, values: visibilityData, checkboxesState }, reactDispatch] = useReducer(
        checkInFieldsReducer,
        {
            selectedDocumentType: getSelectedDocumentType(initialValues),
            values: initialValues,
            checkboxesState: calculateCheckboxesState(initialValues),
        }
    );
    const localizeText = useLocalizeText();

    const setDocumentsVisibilityData = (visibility: CheckInConfigurationValue) => {
        reactDispatch({ type: 'CHANGE_DOCUMENT_VISIBILITY', payload: { visibility } });
    };

    const setDocumentType = (documentType: CheckInConfigurationType | null) => {
        reactDispatch({ type: 'CHANGE_DOCUMENT_TYPE', payload: { documentType } });
    };

    const setVisibilityData = ({ type, value, columnName }: VisibilityData) => {
        reactDispatch({ type: 'CHANGE_VISIBILITY_DATA', payload: { type, value, columnName } });
    };
    const checkboxTabValues = checkboxesState[activeTabValue];

    const changeCheckboxesState = (checkboxValues: CheckboxValues, tabValue: TabItemValue) => {
        reactDispatch({ type: 'CHANGE_CHECKBOX_STATE', payload: { tabValue, checkboxValues } });
    };

    const setCheckboxesState = (
        changedCheckboxesState: CheckInCheckboxStates,
        changedCheckboxColumn: CheckInCheckboxColumnName
    ) => {
        reactDispatch({
            type: 'SET_CHECKBOX_STATE',
            payload: { checkboxesState: changedCheckboxesState, changedCheckboxColumn },
        });
    };

    const displayInfoMessage = () =>
        showInfoAlert({
            title: lt('CheckInFieldsTabChangesAlertTitle'),
            description: lt('CheckInFieldsTabChangesAlertDescription'),
        });

    const modifyCheckboxesFromGeneralToOtherTabs = (
        checkboxValues: CheckboxValues,
        changedCheckboxColumn: 'reservationAdult' | 'reservationChild'
    ) => {
        const otherTabs = {
            [TabItemValue.Address]: checkboxesState[TabItemValue.Address][changedCheckboxColumn],
            [TabItemValue.Documents]: checkboxesState[TabItemValue.Documents][changedCheckboxColumn],
            [TabItemValue.GuestVerification]: checkboxesState[TabItemValue.GuestVerification][changedCheckboxColumn],
        };

        const hasOtherTabsEnabled = Object.values(otherTabs).some((value) => value);

        if (
            checkboxesState[TabItemValue.General][changedCheckboxColumn] && // was true
            !checkboxValues[changedCheckboxColumn] && // now is false
            hasOtherTabsEnabled
        ) {
            Object.keys(otherTabs).forEach((key) => {
                changeCheckboxesState({ ...checkboxesState[key], [changedCheckboxColumn]: false }, key as TabItemValue);
            });
            displayInfoMessage();
        }

        const previouslyModifiedValues = calculateCheckboxesState(visibilityData);

        // When was true, and now is true again ( toggled back to true )
        if (
            checkboxValues[changedCheckboxColumn] &&
            previouslyModifiedValues[TabItemValue.General][changedCheckboxColumn]
        ) {
            setCheckboxesState(previouslyModifiedValues, changedCheckboxColumn);
        }
    };

    const modifyCheckboxesFromOtherTabsToGeneral = (
        updatedCheckboxValues: CheckboxValues,
        changedColumn: CheckInCheckboxColumnName
    ) => {
        if (
            !checkboxesState[TabItemValue.General][changedColumn] && // General column was false
            updatedCheckboxValues[changedColumn] // and some other tab column is true
        ) {
            updateSectionValues({
                updatedCheckboxValues,
                changedColumn,
                sourceTab: TabItemValue.General,
                changedFromOutside: true,
            });
            changeCheckboxesState(updatedCheckboxValues, TabItemValue.General);
            displayInfoMessage();
        }
    };

    interface GetFieldDataProps {
        sourceTab: TabItemValue;
        documentsFieldData?: FieldData[];
    }

    const getFieldData = ({ sourceTab, documentsFieldData }: GetFieldDataProps) => {
        /**
            If the source tab is documents, we need to add the selected document type to the field data in the right format
            textKey isn't used anywhere
         */
        if (sourceTab === TabItemValue.Documents && documentsFieldData != null && selectedDocumentType != null) {
            return [
                ...documentsFieldData,
                {
                    type: selectedDocumentType,
                    textKey: supportedDocumentTypeOptions[selectedDocumentType],
                },
            ];
        }

        return getSection(sourceTab, {
            guestVerificationPhotoEnabled,
            areExtendedHotelConfiguredFieldsEnabled,
            isIdentityDocumentScanEnabled,
            isIdentityDocumentScanVerificationEnabled,
        });
    };

    interface SectionValuesOptions {
        updatedCheckboxValues: CheckboxValues;
        changedColumn: CheckInCheckboxColumnName;
        sourceTab: TabItemValue;
        changedFromOutside: boolean;
        documentsFieldData?: FieldData[];
    }
    const updateSectionValues = ({
        updatedCheckboxValues,
        changedColumn,
        sourceTab,
        changedFromOutside = false,
        documentsFieldData,
    }: SectionValuesOptions) => {
        const fieldData = getFieldData({ sourceTab, documentsFieldData });

        const wasAllFieldsHidden = pipe(
            fieldData,
            array.filterMap(({ type }) =>
                initialValues[type] != null ? option.some(initialValues[type]) : option.none
            ),
            array.every((config) => config[changedColumn] === CheckInConfigurationValue.DoNotShow)
        );

        if (updatedCheckboxValues[changedColumn] && wasAllFieldsHidden) {
            fieldData.forEach(({ type }) => {
                if (initialValues[type] != null) {
                    const documentTypeValueOrDefault =
                        type === selectedDocumentType
                            ? visibilityData[selectedDocumentType].reservationOwner
                            : CheckInConfigurationValue.Default;

                    const enabledCheckboxValue = changedFromOutside
                        ? CheckInConfigurationValue.DoNotShow
                        : documentTypeValueOrDefault;

                    setVisibilityData({
                        type,
                        columnName: changedColumn,
                        value:
                            type === CheckInConfigurationType.LastName
                                ? CheckInConfigurationValue.Required
                                : enabledCheckboxValue,
                    });
                }
            });
        }
    };

    const changeOtherTabsCheckboxesState = ({
        checkboxValues,
        changedColumn,
        sourceTab,
        documentsFieldData,
    }: ChangeCheckboxesStateProps) => {
        updateSectionValues({
            updatedCheckboxValues: checkboxValues,
            changedColumn,
            sourceTab,
            documentsFieldData,
            changedFromOutside: false,
        });
        changeCheckboxesState(checkboxValues, sourceTab);

        if (sourceTab === TabItemValue.General) {
            modifyCheckboxesFromGeneralToOtherTabs(checkboxValues, changedColumn);
            return;
        }

        modifyCheckboxesFromOtherTabsToGeneral(checkboxValues, changedColumn);
    };

    const handleSave = () => {
        onSubmit(visibilityData, checkboxesState);
    };

    const mainTable = (() => {
        switch (activeTabValue) {
            case TabItemValue.Documents: {
                const documentTableData = [
                    {
                        type: selectedDocumentType,
                        tooltip:
                            selectedDocumentType != null
                                ? tooltipValues[selectedDocumentType].reservationOwner
                                : ({ type: 'empty' } satisfies ConfigurationTooltip),
                        fieldName:
                            selectedDocumentType != null
                                ? localizeText({ textKey: supportedDocumentTypeOptions[selectedDocumentType] })
                                : null,
                        visibility: getCheckInSameValues(
                            selectedDocumentType != null
                                ? visibilityData[selectedDocumentType].reservationOwner
                                : CheckInConfigurationValue.Default
                        ),
                        setDocumentType,
                        isVisibilityDisabled: selectedDocumentType == null,
                        setDocumentsVisibilityData,
                    },
                ];
                return <DocumentsTable data={documentTableData} />;
            }
            default:
                return (
                    <CheckInFieldsTable
                        initialValues={initialValues}
                        editableValues={editableValues}
                        isLegallyRequiredRecord={isLegallyRequiredRecord}
                        section={getSection(activeTabValue, {
                            guestVerificationPhotoEnabled,
                            areExtendedHotelConfiguredFieldsEnabled,
                            isIdentityDocumentScanEnabled,
                            isIdentityDocumentScanVerificationEnabled,
                        })}
                        activeTabValue={activeTabValue}
                        setVisibilityData={setVisibilityData}
                        visibilityData={visibilityData}
                        tooltipValues={tooltipValues}
                        changeCheckboxesState={changeOtherTabsCheckboxesState}
                        checkboxValues={checkboxTabValues}
                    />
                );
        }
    })();

    const documentSections =
        activeTabValue === TabItemValue.Documents &&
        selectedDocumentType != null &&
        visibilityData[selectedDocumentType].reservationOwner !== CheckInConfigurationValue.DoNotShow ? (
            <DocumentSectionTables
                initialValues={initialValues}
                editableValues={editableValues}
                isLegallyRequiredRecord={isLegallyRequiredRecord}
                setVisibilityData={setVisibilityData}
                visibilityData={visibilityData}
                selectedDocumentType={selectedDocumentType}
                tooltipValues={tooltipValues}
                activeTabValue={activeTabValue}
                changeCheckboxesState={changeOtherTabsCheckboxesState}
                checkboxValues={checkboxTabValues}
            />
        ) : null;

    return (
        <Stack spacing="400" vertical>
            <Stack vertical spacing="300">
                <CheckInFieldsHeader onSave={handleSave} />
                <Card padding="100">
                    <Stack vertical spacing="300">
                        <CheckInTabs activeTabValue={activeTabValue} onTabClick={setActiveTabValue} />
                        {mainTable}
                    </Stack>
                </Card>
            </Stack>
            {documentSections}
        </Stack>
    );
};
